import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import {
  confirmSourceFromEmailNotification,
  setConfirmCandidateSource,
} from '~/domain/ConfirmCandidateSource';
import { buildEngagementContext, parseNotificationParams } from '~/domain/OfficeNotification';
import { useOfficeNotificationLanding } from '~/hooks/useOfficeNotificationLanding';
import type { IJob } from '~/models';
import {
  fireOfficeNotificationCandidateApplied,
  fireOfficeNotificationCandidateAppliedCounter,
} from '~/posthogs/events';

interface IUseCandidateAppliedLandingArgs {
  /** The shift the email links to. Nothing is attributed until it has loaded. */
  job?: IJob;
  /**
   * Whether the redesign owns this landing. False wherever the legacy screen
   * still renders and attributes it itself, so nothing is reported twice.
   */
  enabled: boolean;
}

/**
 * Attributes a "candidate applied" email landing on the shift route, for every
 * status the redesign renders (GOTU-4906, GOTU-4002).
 *
 * The email links to `/dashboard/job/:jobId` whatever the shift's status, and
 * the legacy route attributed it there, before redirecting to the confirm
 * screen (`OpenJobScreenSwitch`, `OpenJobScreen`). With the flag on, the
 * redesign picks a page by status instead, so attribution has to sit at the
 * route as well: a pending shift lands on the Confirm Candidates page, which
 * would otherwise record nothing.
 *
 * Does two things, as the legacy route did: records the confirm source, so a
 * later confirm reports the email, and fires the `office_notification` landing
 * event.
 */
export function useCandidateAppliedLanding({ job, enabled }: IUseCandidateAppliedLandingArgs) {
  const [searchParams] = useSearchParams();
  const isReady = enabled && Boolean(job);

  // Captured on first render, while the job is still loading: the landing event
  // below strips the params from the URL once it fires. Gated on `origin` so
  // only a real email landing (`utm_source=email`) is attributed to email.
  const [emailSource] = useState(() => {
    const { origin, notification } = parseNotificationParams(searchParams);
    return origin === 'email' ? confirmSourceFromEmailNotification(notification) : undefined;
  });
  const hasRecordedRef = useRef(false);

  useEffect(() => {
    if (!isReady || !job || !emailSource || hasRecordedRef.current) {
      return;
    }

    hasRecordedRef.current = true;
    setConfirmCandidateSource({ source: emailSource, shiftId: job.id });
  }, [emailSource, isReady, job]);

  useOfficeNotificationLanding({
    enabled: isReady,
    fallbackKey: 'candidate_applied',
    handlers: {
      candidate_applied: ({ origin, clickedAt, shiftIds, professionalId }) =>
        fireOfficeNotificationCandidateApplied({
          origin,
          shiftId: shiftIds[0] ?? job?.id,
          professionalId,
          engagement: buildEngagementContext({ clickedAt, shiftStartAt: job?.job_datetime }),
        }),
      candidate_applied_counter: ({ origin, clickedAt, shiftIds, professionalId }) =>
        fireOfficeNotificationCandidateAppliedCounter({
          origin,
          shiftId: shiftIds[0] ?? job?.id,
          professionalId,
          engagement: buildEngagementContext({ clickedAt, shiftStartAt: job?.job_datetime }),
        }),
    },
  });
}
